import { calculateDeliveryProposalOfferCargoStatus } from "@/features/offer-cargo-process-status";
import { ScrollToTop } from "@/shared/lib/ScrollToTop";
import { BaseKeyValue } from "@/shared/ui/BaseKeyValue";
import { BaseBreadCrumb } from "@/shared/ui/base-bread-crumb";
import { BaseDivider } from "@/shared/ui/base-divider";
import { BaseLink } from "@/shared/ui/base-link";
import { BaseStepper } from "@/shared/ui/base-stepper";
import { DeliveryProposalRejectionDialog } from "@/widgets/delivery-proposal-rejection-dialog";
import { OfferCargoCargoInfo } from "@/widgets/offer-cargo-cargo-info";
import { OfferCargoDestinationsInfo } from "@/widgets/offer-cargo-destinations-info";
import { OfferCargoOfficeInfo } from "@/widgets/offer-cargo-office-info";
import { OfferCargoScheduleInfo } from "@/widgets/offer-cargo-schedule-info";
import { OfferCargoShippingFeeInfo } from "@/widgets/offer-cargo-shipping-fee-info";
import { OfferCargoStatusCard } from "@/widgets/offer-cargo-status-card/ui";
import { OfferCargoTaskCard } from "@/widgets/offer-cargo-task-card";
import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import type { CarrierCargoDetailPageProps, ScheduledCarrier } from "../model";
import { ConfirmDeleteScheduledCarrierDialog } from "./confirm-delete-scheduled-carrier-dialog";
import { ScheduledCarrierFormDialog } from "./scheduled-carrier-form-dialog";
import { ScheduledCarrierSection } from "./scheduled-carrier-section";

export const CarrierCargoDetailPageUi = (
  props: CarrierCargoDetailPageProps,
) => {
  const {
    offerCargoProcess,
    myLatestValidDeliveryProposalOfferCargo,
    latestOnGoingContractOfferCargo,
    vehicles,
  } = props;
  const links = [
    {
      // すでに提案済みの場合「提案済み荷物」一覧に戻る
      href: myLatestValidDeliveryProposalOfferCargo.data
        ? "/carrier/cargos?tab=proposed"
        : "/carrier/cargos?tab=not_proposed",
      label: "荷物一覧",
    },
    { label: "荷物詳細" },
  ];

  const navigate = useNavigate({ from: "/carrier/cargos/$id" });
  const [showContractRejectionDialog, setShowContractRejectionDialog] =
    useState(false);

  // 配送予定車両関連の状態
  const [showScheduledCarrierDialog, setShowScheduledCarrierDialog] =
    useState(false);
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);
  const [scheduledCarrierDialogMode, setScheduledCarrierDialogMode] = useState<
    "create" | "edit"
  >("create");
  const [editingScheduledCarrier, setEditingScheduledCarrier] =
    useState<ScheduledCarrier>();
  const [isSubmittingScheduledCarrier, setIsSubmittingScheduledCarrier] =
    useState(false);

  // 配送予定車両のハンドラー
  const handleAddScheduledCarrier = () => {
    setEditingScheduledCarrier(undefined);
    setScheduledCarrierDialogMode("create");
    setShowScheduledCarrierDialog(true);
  };

  const handleEditScheduledCarrier = () => {
    setEditingScheduledCarrier(
      latestOnGoingContractOfferCargo.data?.scheduledCarrier,
    );
    setScheduledCarrierDialogMode("edit");
    setShowScheduledCarrierDialog(true);
  };

  const handleDeleteScheduledCarrier = () => {
    setShowDeleteConfirmDialog(true);
  };

  const handleSubmitScheduledCarrier = async (
    data: Parameters<
      CarrierCargoDetailPageProps["onCreateScheduledCarrier"]
    >[0],
  ) => {
    setIsSubmittingScheduledCarrier(true);
    try {
      if (scheduledCarrierDialogMode === "create") {
        await props.onCreateScheduledCarrier(data);
      } else {
        await props.onUpdateScheduledCarrier(data);
      }
      setShowScheduledCarrierDialog(false);
    } finally {
      setIsSubmittingScheduledCarrier(false);
    }
  };

  const handleConfirmDelete = async () => {
    await props.onDeleteScheduledCarrier();
    setShowDeleteConfirmDialog(false);
  };

  const deliveryProposalOfferCargoStatus =
    offerCargoProcess.data && myLatestValidDeliveryProposalOfferCargo.data
      ? calculateDeliveryProposalOfferCargoStatus({
          process: offerCargoProcess.data,
          deliveryProposalOfferCargoId:
            myLatestValidDeliveryProposalOfferCargo.data.id,
        })
      : undefined;

  const STEPPER_LIST = ["配送提案", "契約合意", "配送確認"];
  const currentStep = useMemo(() => {
    if (!deliveryProposalOfferCargoStatus) {
      return -1;
    }
    switch (deliveryProposalOfferCargoStatus) {
      case "no_valid_contracts":
      case "delivery_proposal_rejected":
      case "has_contracts_all_rejected":
        return 0;
      case "has_contract_pending":
        return 1;
      case "delivery_proposal_response_deadline_expired":
        return -1;
      case "has_contract_concluded":
        return 2;
      default:
        return -1;
    }
  }, [deliveryProposalOfferCargoStatus]);

  return (
    <Box px={{ xs: 2, sm: 4 }} pb={10}>
      <ScrollToTop />
      <BaseBreadCrumb links={links} sx={{ mb: 3 }} />
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          荷物詳細
        </Typography>
      </Box>
      {offerCargoProcess.status === "error" ? (
        <Stack width="100%" justifyContent="center" alignItems="center" py={18}>
          <Typography variant="body1" color="error">
            エラーが発生しました
          </Typography>
          <Typography variant="caption" color="error">
            {offerCargoProcess.error?.message}
          </Typography>
        </Stack>
      ) : offerCargoProcess.status === "loading" ? (
        <Stack width="100%" justifyContent="center" alignItems="center" py={18}>
          <CircularProgress />
        </Stack>
      ) : (
        (() => {
          return (
            <Stack mt={{ xs: 3, sm: 4 }} spacing={{ xs: 3, sm: 4 }}>
              {myLatestValidDeliveryProposalOfferCargo.data && (
                <BaseStepper
                  currentStep={currentStep}
                  stepperList={STEPPER_LIST}
                />
              )}
              <DeliveryProposalRejectionDialog
                open={showContractRejectionDialog}
                onClose={() => setShowContractRejectionDialog(false)}
                onSubmit={(rejectParams) => {
                  props.onRejectContractClick(rejectParams);
                  setShowContractRejectionDialog(false);
                }}
              />
              <OfferCargoStatusCard
                variant="carrier_cargo_detail"
                deliveryProposalOfferCargoId={
                  myLatestValidDeliveryProposalOfferCargo.data?.id
                }
                offerCargoProcess={offerCargoProcess.data}
                onProposeClick={props.onProposeClick}
                onApproveContractClick={props.onApproveContractClick}
                onRejectContractClick={() =>
                  setShowContractRejectionDialog(true)
                }
                onProposeAgainClick={props.onProposeAgainClick}
              />
              <OfferCargoTaskCard
                viewer="carrier"
                deliveryProposalOfferCargoId={
                  myLatestValidDeliveryProposalOfferCargo.data?.id
                }
                offerCargoProcess={offerCargoProcess.data}
                onShowDeliveryRequestFilesClick={() =>
                  navigate({ hash: "delivery-request-files", replace: true })
                }
              />
              <BaseDivider />
              {latestOnGoingContractOfferCargo.data?.stage ===
                "contract_concluded" && (
                <>
                  <ScheduledCarrierSection
                    scheduledCarrier={
                      latestOnGoingContractOfferCargo.data.scheduledCarrier
                    }
                    onEdit={handleEditScheduledCarrier}
                    onDelete={handleDeleteScheduledCarrier}
                    onAdd={handleAddScheduledCarrier}
                  />
                  <ScheduledCarrierFormDialog
                    open={showScheduledCarrierDialog}
                    onClose={() => setShowScheduledCarrierDialog(false)}
                    onSubmit={handleSubmitScheduledCarrier}
                    editingData={editingScheduledCarrier}
                    vehicles={vehicles.data || []}
                    isSubmitting={isSubmittingScheduledCarrier}
                    mode={scheduledCarrierDialogMode}
                  />
                  <ConfirmDeleteScheduledCarrierDialog
                    open={showDeleteConfirmDialog}
                    onClose={() => setShowDeleteConfirmDialog(false)}
                    onDelete={handleConfirmDelete}
                    vehicleName={
                      editingScheduledCarrier?.vehicleNumber ?? undefined
                    }
                    driverName={
                      editingScheduledCarrier?.contactName ?? undefined
                    }
                  />
                  <BaseDivider />
                </>
              )}
              {latestOnGoingContractOfferCargo.data?.stage ===
                "contract_concluded" && (
                <>
                  <Box id="delivery-request-files" maxWidth="628px">
                    <Typography variant="h6" fontWeight={700}>
                      配送依頼書
                    </Typography>
                  </Box>
                  {latestOnGoingContractOfferCargo.data
                    .attachedDeliveryRequestFiles &&
                    latestOnGoingContractOfferCargo.data
                      .attachedDeliveryRequestFiles.length > 0 && (
                      <BaseKeyValue keyLabel="添付ファイル">
                        <Stack>
                          {latestOnGoingContractOfferCargo.data.attachedDeliveryRequestFiles.map(
                            (file) => (
                              <BaseLink href={file.url} key={file.url}>
                                {file.filename}
                              </BaseLink>
                            ),
                          )}
                        </Stack>
                      </BaseKeyValue>
                    )}
                  <BaseDivider />
                </>
              )}
              <Typography variant="h6" fontWeight={700}>
                {!latestOnGoingContractOfferCargo.data
                  ? "荷物登録内容"
                  : "契約内容"}
              </Typography>
              <OfferCargoShippingFeeInfo
                viewer="carrier"
                offerCargoProcess={offerCargoProcess.data}
                deliveryProposalOfferCargoId={
                  myLatestValidDeliveryProposalOfferCargo.data?.id
                }
              />
              <OfferCargoScheduleInfo
                draftOfferCargo={offerCargoProcess.data.draftOfferCargo}
              />
              <OfferCargoDestinationsInfo
                variant="carrier_cargo_detail"
                draftOfferCargo={offerCargoProcess.data.draftOfferCargo}
                deliveryProposalOfferCargo={
                  myLatestValidDeliveryProposalOfferCargo.data
                }
              />
              <BaseDivider />
              <OfferCargoCargoInfo
                baseOfferCargo={
                  latestOnGoingContractOfferCargo.data ??
                  offerCargoProcess.data.draftOfferCargo
                }
                deliveryProposalOfferCargo={
                  latestOnGoingContractOfferCargo
                    ? undefined
                    : myLatestValidDeliveryProposalOfferCargo.data
                }
                viewer="carrier"
              />
              <BaseDivider />
              <OfferCargoOfficeInfo
                variant="carrier_cargo_detail"
                offerCargoProcess={offerCargoProcess.data}
                latestOnGoingContractOfferCargo={
                  latestOnGoingContractOfferCargo.data
                }
              />
            </Stack>
          );
        })()
      )}
    </Box>
  );
};
